/**
 * DateShake Legal Agreements Component
 * Copyright (c) 2024 DateShake. All rights reserved.
 * 
 * Handles Terms of Service and Privacy Policy display and acceptance
 */

import { useState } from 'react'

interface LegalAgreementsProps {
  onAccept: (accepted: boolean) => void
  required?: boolean
  includeAgeVerification?: boolean
}

export default function LegalAgreements({ onAccept, required = true, includeAgeVerification = true }: LegalAgreementsProps) {
  const [accepted, setAccepted] = useState(false)
  const [ageVerified, setAgeVerified] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [showPrivacy, setShowPrivacy] = useState(false)

  const handleAcceptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isAccepted = e.target.checked
    setAccepted(isAccepted)
    onAccept(isAccepted && (!includeAgeVerification || ageVerified))
  }

  const handleAgeVerificationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isAgeVerified = e.target.checked
    setAgeVerified(isAgeVerified)
    onAccept(accepted && isAgeVerified)
  }

  return (
    <div className="space-y-3">
      {includeAgeVerification && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <div className="flex items-start space-x-2">
            <input
              type="checkbox"
              id="age-verification"
              checked={ageVerified}
              onChange={handleAgeVerificationChange}
              className="mt-1 h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
              required={required}
            />
            <label htmlFor="age-verification" className="text-sm text-amber-800 leading-relaxed">
              <strong>Age Verification:</strong> I confirm that I am 18 years of age or older and understand that DateShake is intended for adults only.
            </label>
          </div>
        </div>
      )}
      
      <div className="flex items-start space-x-2">
        <input
          type="checkbox"
          id="legal-agreement"
          checked={accepted}
          onChange={handleAcceptChange}
          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          required={required}
        />
        <label htmlFor="legal-agreement" className="text-sm text-gray-700 leading-relaxed">
          I agree to the{' '}
          <button
            type="button"
            onClick={() => setShowTerms(true)}
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Terms of Service
          </button>{' '}
          and{' '}
          <button
            type="button"
            onClick={() => setShowPrivacy(true)}
            className="text-blue-600 hover:text-blue-800 underline"
          >
            Privacy Policy
          </button>
        </label>
      </div>

      {/* Terms of Service Modal */}
      {showTerms && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-black">Terms of Service</h2>
                <button
                  onClick={() => setShowTerms(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
              <div className="text-sm text-gray-700 space-y-4">
                <p><strong>Last updated:</strong> {new Date().toLocaleDateString()}</p>
                
                <section>
                  <h3 className="font-semibold text-black mb-2">1. Acceptance of Terms</h3>
                  <p>By accessing and using DateShake, you accept and agree to be bound by the terms and provision of this agreement.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-black mb-2">2. Use License</h3>
                  <p>Permission is granted to temporarily download one copy of DateShake for personal, non-commercial transitory viewing only.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-black mb-2">3. User Accounts</h3>
                  <p>You are responsible for maintaining the confidentiality of your account and password. You agree to accept responsibility for all activities that occur under your account.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-black mb-2">4. Prohibited Uses</h3>
                  <p>You may not use DateShake for any unlawful purpose or to solicit others to perform unlawful acts.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-black mb-2">5. Content</h3>
                  <p>DateShake does not warrant that the information on this website is accurate, complete, or current.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-black mb-2">6. Privacy</h3>
                  <p>Your privacy is important to us. Please review our Privacy Policy, which also governs your use of the service.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-black mb-2">7. Termination</h3>
                  <p>We may terminate or suspend your account immediately, without prior notice, for conduct that we believe violates these Terms of Service.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-black mb-2">8. Changes to Terms</h3>
                  <p>We reserve the right to modify these terms at any time. We will notify users of any material changes via email or through the service.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-black mb-2">9. Contact Information</h3>
                  <p>If you have any questions about these Terms of Service, please contact us at support@dateshake.com</p>
                </section>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowTerms(false)}
                  className="btn-primary px-6 py-2"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacy && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-black">Privacy Policy</h2>
                <button
                  onClick={() => setShowPrivacy(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>
              <div className="text-sm text-gray-700 space-y-4">
                <p><strong>Last updated:</strong> {new Date().toLocaleDateString()}</p>
                
                <section>
                  <h3 className="font-semibold text-black mb-2">1. Information We Collect</h3>
                  <p>We collect information you provide directly to us, such as when you create an account, update your profile, or use our services.</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Account information (name, email, password)</li>
                    <li>Profile information (birthday, location, preferences)</li>
                    <li>Usage data and analytics</li>
                  </ul>
                </section>

                <section>
                  <h3 className="font-semibold text-black mb-2">2. How We Use Your Information</h3>
                  <p>We use the information we collect to:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Provide, maintain, and improve our services</li>
                    <li>Personalize your experience</li>
                    <li>Communicate with you about our services</li>
                    <li>Ensure security and prevent fraud</li>
                  </ul>
                </section>

                <section>
                  <h3 className="font-semibold text-black mb-2">3. Information Sharing</h3>
                  <p>We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except as described in this policy.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-black mb-2">4. Data Security</h3>
                  <p>We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-black mb-2">5. Your Rights</h3>
                  <p>You have the right to access, update, or delete your personal information. You can do this through your account settings or by contacting us.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-black mb-2">6. Cookies and Tracking</h3>
                  <p>We use cookies and similar technologies to enhance your experience and analyze usage patterns.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-black mb-2">7. Third-Party Services</h3>
                  <p>We may use third-party services for analytics, authentication, and other features. These services have their own privacy policies.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-black mb-2">8. Age Requirements</h3>
                  <p>DateShake is intended for adults 18 years and older. By using this service, you represent and warrant that you are at least 18 years old. We do not knowingly collect personal information from individuals under 18 years of age.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-black mb-2">9. Changes to This Policy</h3>
                  <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page.</p>
                </section>

                <section>
                  <h3 className="font-semibold text-black mb-2">10. Contact Us</h3>
                  <p>If you have any questions about this Privacy Policy, please contact us at privacy@dateshake.com</p>
                </section>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowPrivacy(false)}
                  className="btn-primary px-6 py-2"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
